import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChatMessage, SendContext } from '../types/chat'
import { insertSanitizedHtml } from '../utils/word/insert-content'
import { useNotification } from './useNotification'

export const useMessageActions = (
  messages: ChatMessage[],
  onEditMessage?: (content: string, sendContext?: SendContext) => void
) => {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const handleCopy = useCallback(
    async (messageId: string) => {
      const message = messages.find((m) => m.id === messageId)
      if (!message) {
        console.warn(`Message with id ${messageId} not found`)
        return
      }

      try {
        if (navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([
            new window.ClipboardItem({
              'text/html': new Blob([message.content], { type: 'text/html' }),
            }),
          ])
        }
      } catch {
        notify({ isSuccess: false, text: t('messageActions.copyFailure') })
      }
    },
    [messages, notify, t]
  )

  const handleInsert = useCallback(
    async (messageId: string) => {
      const message = messages.find((m) => m.id === messageId)
      if (!message) {
        console.warn(`Message with id ${messageId} not found`)
        return
      }

      try {
        await insertSanitizedHtml(message.content, {
          telemetryTask: message.telemetryTask,
        })
      } catch (error) {
        console.error('[Scribbler] Insert into document failed:', error)
        notify({ isSuccess: false, text: t('messageActions.insertFailure') })
      }
    },
    [messages, notify, t]
  )

  // Load message HTML into editor; original message preserved in history
  const handleEdit = useCallback(
    (messageId: string) => {
      const message = messages.find((m) => m.id === messageId)
      if (!message) {
        console.warn(`Message with id ${messageId} not found`)
        notify({ isSuccess: false, text: t('messageActions.editFailure') })
        return
      }

      try {
        if (onEditMessage) {
          onEditMessage(message.content, message.sendContext)
          notify({ isSuccess: true, text: t('messageActions.editSuccess'), position: 'top' })
        }
      } catch (error) {
        console.error('[Scribbler] Edit message failed:', error)
        notify({ isSuccess: false, text: t('messageActions.editFailure') })
      }
    },
    [messages, notify, onEditMessage, t]
  )

  return { handleCopy, handleInsert, handleEdit }
}
